
import React, { useState } from 'react';
import { Sidebar } from './Sidebar';
import { MobileHeader } from './header/MobileHeader';
import { MobileDrawer } from './navigation/MobileDrawer';
import { DrawerContent } from './navigation/DrawerContent';
import { DrawerProvider, useDrawer } from '@/contexts/DrawerContext';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/hooks/useLanguage';
import { NovidadesModal } from '@/components/novidades/NovidadesModal';
import { ReplicModal } from '@/components/tools/ReplicModal';
import { FunnelXModal } from '@/components/tools/FunnelXModal';
import { AdSniperModal } from '@/components/tools/AdSniperModal';

type LayoutProps = {
  children: React.ReactNode;
  fixedSidebar?: boolean;
  hasUnsavedChanges?: boolean;
  onSave?: () => void;
  isSaving?: boolean;
  isSaved?: boolean;
  videoId?: string;
  videoName?: string;
  userId?: string;
  onCreateFolder?: () => void;
  haveHeadline?: boolean;
  isHTMLSelected?: boolean;
  headlineHtmlCode?: string;
  // Personalization.tsx já passava estas três; faltava declará-las aqui.
  headlineShowThroughout?: boolean;
  headlineInitialDelay?: string;
  headlineEndDelay?: string;
};

function LayoutContent({ children, hasUnsavedChanges, onSave, isSaving, isSaved, videoId, videoName, userId, onCreateFolder, haveHeadline, isHTMLSelected, headlineHtmlCode }: LayoutProps) {
  useLanguage(); // re-renderiza o layout ao trocar idioma, propagando para todos os filhos
  const { isOpen, closeDrawer, toggleDrawer } = useDrawer();
  const location = useLocation();
  const [isNovidadesOpen, setIsNovidadesOpen] = useState(false);
  const [isReplicOpen, setIsReplicOpen] = useState(false);
  const [isFunnelXOpen, setIsFunnelXOpen] = useState(false);
  const [isAdSniperOpen, setIsAdSniperOpen] = useState(false);

  // Detectar rotas onde a sidebar principal deve ser ocultada
  const isPersonalizationsRoute = location.pathname.startsWith('/personalizations');
  const isNewVideoRoute = location.pathname.startsWith('/new-video');

  // A home (dashboard) desenha o próprio fundo; as demais rotas continuam flat.
  // Atenção: aqui '/' é o Login e '/meus-videos' é o dashboard LEGADO (que não
  // desenha fundo próprio) — só '/dashboard' usa o main transparente.
  const isDashboardRoute = location.pathname === '/dashboard';

  // Telas de recurso do menu lateral + conta do usuário: o conteúdo vira um card
  // flutuante sobre o canvas cinza. Lista explícita de propósito — checkout e telas
  // de admin continuam flat, então não dá para inferir por "tudo que não é dashboard".
  const FLOATING_PANEL_ROUTES = [
    '/shared',
    '/distribuir',
    '/vsleads',
    '/vsl-dinamica',
    '/metrics',
    '/tempo-real',
    '/tracking',
    '/comparar-vsl',
    '/ab-test',
    '/hook-track',
    '/vsl-cloaker',
    '/pixel',
    '/domain-protection',
    '/api-keys',
    '/academy',
    '/partners',
    '/finance',
    '/profile',
    '/afiliate',
    '/networkList',
    '/members',
  ];
  const isFloatingPanelRoute = FLOATING_PANEL_ROUTES.some(
    route => location.pathname === route || location.pathname.startsWith(`${route}/`)
  );

  return (
    <>
      <NovidadesModal open={isNovidadesOpen} onOpenChange={setIsNovidadesOpen} />
      <ReplicModal open={isReplicOpen} onOpenChange={setIsReplicOpen} />
      <FunnelXModal open={isFunnelXOpen} onOpenChange={setIsFunnelXOpen} />
      <AdSniperModal open={isAdSniperOpen} onOpenChange={setIsAdSniperOpen} />

      {/* Mobile/Tablet Layout */}
      <div className="lg:hidden flex flex-col w-full h-screen overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        <MobileHeader
          onDrawerToggle={toggleDrawer}
          hasUnsavedChanges={hasUnsavedChanges}
          onSave={onSave}
          isSaving={isSaving}
          isSaved={isSaved}
          videoId={videoId}
          videoName={videoName}
          userId={userId}
          onCreateFolder={onCreateFolder}
          haveHeadline={haveHeadline}
          isHTMLSelected={isHTMLSelected}
          headlineHtmlCode={headlineHtmlCode}
        />

        {/* Drawer condicional: recursos de personalização ou menu do dashboard */}
        <MobileDrawer
          isOpen={isOpen}
          onClose={closeDrawer}
          customLayout={isPersonalizationsRoute || isNewVideoRoute}
          key={`drawer-${isPersonalizationsRoute || isNewVideoRoute ? 'personalization' : 'dashboard'}`}
        >
          {isPersonalizationsRoute || isNewVideoRoute ? (
            // Container para conteúdo injetado via portal do NewVideoLayout
            <div
              id="personalization-drawer-content"
              className="h-full"
              key="drawer-content-container"
            />
          ) : (
            <DrawerContent
              onNavigate={closeDrawer}
              onOpenNovidades={() => setIsNovidadesOpen(true)}
              onOpenReplic={() => setIsReplicOpen(true)}
              onOpenFunnelX={() => setIsFunnelXOpen(true)}
              onOpenAdSniper={() => setIsAdSniperOpen(true)}
            />
          )}
        </MobileDrawer>

        {/* O padding do topo compensa o MobileHeader, que é fixed — mantenha os
            dois com o mesmo valor (hoje 64px), senão o conteúdo some atrás dele. */}
        <main className={cn(
          "flex-1 w-full pb-[env(safe-area-inset-bottom)]",
          !(isPersonalizationsRoute || isNewVideoRoute) && "pt-[64px]"
        )}>
          {children}
        </main>
      </div>

      {/* Desktop Layout — o fundo cinza é o papel de parede atrás da sidebar flutuante */}
      <div className="hidden lg:block min-h-screen bg-[#F6F8FC] w-full">
        {/* Sidebar principal - oculta em /personalizations e /new-video */}
        {!isPersonalizationsRoute && !isNewVideoRoute && <Sidebar />}

        <main className={cn(
          "flex flex-col min-h-screen",
          isPersonalizationsRoute || isNewVideoRoute
            ? "bg-background"
            : isDashboardRoute
              // Home: a página desenha o próprio fundo
              ? "lg:ml-[278px] lg:mr-0 lg:min-h-screen bg-transparent border-0"
              : isFloatingPanelRoute
                // Telas de recurso: card flutuante sobre o canvas cinza
                ? "lg:ml-[268px] lg:mr-[10px] lg:my-[10px] lg:min-h-[calc(100vh-20px)] bg-background rounded-[16px] border border-[#E1E7F0] shadow-[0_8px_30px_rgba(15,23,42,0.08)] overflow-hidden"
                // Demais rotas: flat até a borda direita
                : "lg:ml-[268px] bg-background"
        )}>
          {children}
        </main>
      </div>
    </>
  );
}

export function Layout({
  children,
  fixedSidebar = false,
  hasUnsavedChanges,
  onSave,
  isSaving,
  isSaved,
  videoId,
  videoName,
  userId,
  onCreateFolder,
  haveHeadline,
  isHTMLSelected,
  headlineHtmlCode,
}: LayoutProps) {
  return (
    <DrawerProvider>
      <LayoutContent
        fixedSidebar={fixedSidebar}
        hasUnsavedChanges={hasUnsavedChanges}
        onSave={onSave}
        isSaving={isSaving}
        isSaved={isSaved}
        videoId={videoId}
        videoName={videoName}
        userId={userId}
        onCreateFolder={onCreateFolder}
        haveHeadline={haveHeadline}
        isHTMLSelected={isHTMLSelected}
        headlineHtmlCode={headlineHtmlCode}
      >
        {children}
      </LayoutContent>
    </DrawerProvider>
  );
}
